import React, { useState, useEffect } from 'react';
import { BranchProductAssignInput, Category, Branch } from '../../../types';
import Step1BasicInfo from './steps/Step1BasicInfo';
import Step2PricingCategory from './steps/Step2PricingCategory';
import Step3ProductFeatures from './steps/Step3ProductFeatures';
import Step4BranchAssignment from './steps/Step4BranchAssignment';
import Step5BranchDescriptions from './steps/Step5BranchDescriptions';
import Step6UploadImage from './steps/Step6UploadImage';
import Step7ReviewSubmit from './steps/Step7ReviewSubmit';

// ============================
// Interfaces
// ============================
interface ProductFormProps {
  categories: Category[];
  branches: Branch[];
  onSubmit: (productData: FormData, assignData: BranchProductAssignInput) => void;
  onCancel: () => void;
  isEditing?: boolean;
  initialData?: Partial<{
    name: string;
    sku: string;
    description: string;
    short_description: string;
    base_price: number;
    category: number;
    is_featured: boolean;
    badge: string;
    branch_description: string;
    branch_description_ar: string;
  }>;
}

interface StepProps {
  formData: any;
  onInputChange: (field: string, value: any) => void;
  categories: Category[];
  branches: Branch[];
  image?: File | null;
  imagePreview?: string | null;
  onImageChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isEditing?: boolean;
  isSkuManual?: boolean;
  onSkuManualChange?: (manual: boolean) => void;
}

// ============================
// Steps Configuration
// ============================
const STEPS = [
  { id: 1, title: 'Basic Info', description: 'Product name and details' },
  { id: 2, title: 'Pricing', description: 'Price and category' },
  { id: 3, title: 'Features', description: 'Badges and special features' },
  { id: 4, title: 'Branch', description: 'Assign to branch' },
  { id: 5, title: 'Descriptions', description: 'Branch descriptions' },
  { id: 6, title: 'Image', description: 'Upload product image' },
  { id: 7, title: 'Review', description: 'Review all information' },
];

// ============================
// Main Component
// ============================
const ProductForm: React.FC<ProductFormProps> = ({
  categories,
  branches,
  onSubmit,
  onCancel,
  isEditing = false,
  initialData = {}
}) => {
  // ============================
  // State
  // ============================
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    sku: initialData.sku || '',
    description: initialData.description || '',
    shortDescription: initialData.short_description || '',
    basePrice: initialData.base_price || '',
    category: initialData.category || '',
    isFeatured: initialData.is_featured || false,
    badge: initialData.badge || '',
    branchId: '',
    price: '',
    stock: '',
    isAvailable: true,
    branchDescription: initialData.branch_description || '',
    branchDescriptionAR: initialData.branch_description_ar || '',
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSkuManual, setIsSkuManual] = useState(!!initialData.sku);

  // ============================
  // Effects
  // ============================
  useEffect(() => {
    if (!isSkuManual && formData.name && !initialData.sku) {
      const generatedSku = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s]+/g, '-')
        .replace(/--+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, sku: generatedSku }));
    }
  }, [formData.name, isSkuManual, initialData.sku]);

  // ============================
  // Handlers
  // ============================
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSkuManualChange = (manual: boolean) => setIsSkuManual(manual);

  const nextStep = () => {
    if (currentStep < STEPS.length && validateStep(currentStep)) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('sku', formData.sku);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('short_description', formData.shortDescription);
    formDataToSend.append('base_price', formData.basePrice.toString());
    formDataToSend.append('is_featured', formData.isFeatured.toString());
    formDataToSend.append('badge', formData.badge);
    if (formData.category) formDataToSend.append('categories', formData.category.toString());
    if (image) formDataToSend.append('image', image);

    const assignData: BranchProductAssignInput = {
      product_id: 0,
      branch_id: Number(formData.branchId),
      price: Number(formData.price),
      stock: Number(formData.stock),
      is_available: formData.isAvailable,
      description: formData.branchDescription,
      descriptionAR: formData.branchDescriptionAR,
    };

    onSubmit(formDataToSend, assignData);
  };

  // ============================
  // Validation
  // ============================
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: return formData.name.trim() !== '' && formData.sku.trim() !== '';
      case 2: return formData.basePrice !== '';
      case 3: return true;
      case 4: return formData.branchId !== '' && formData.price !== '' && formData.stock !== '';
      case 5: return true;
      case 6: return isEditing ? true : image !== null;
      case 7: return true;
      default: return false;
    }
  };

  // ============================
  // Render Step
  // ============================
  const renderStep = () => {
    const commonProps: StepProps = {
      formData,
      onInputChange: handleInputChange,
      categories,
      branches,
      image,
      imagePreview,
      onImageChange: handleImageChange,
      isEditing,
      isSkuManual,
      onSkuManualChange: handleSkuManualChange,
    };

    switch (currentStep) {
      case 1: return <Step1BasicInfo {...commonProps} />;
      case 2: return <Step2PricingCategory {...commonProps} />;
      case 3: return <Step3ProductFeatures {...commonProps} />;
      case 4: return <Step4BranchAssignment {...commonProps} />;
      case 5: return <Step5BranchDescriptions {...commonProps} />;
      case 6: return <Step6UploadImage {...commonProps} />;
      case 7: return <Step7ReviewSubmit {...commonProps} />;
      default: return null;
    }
  };

  // ============================
  // JSX
  // ============================
  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Progress Steps */}
      <div className="relative">
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2"></div>
        <div className="relative flex justify-between">
          {STEPS.map(step => (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium mb-2
                  ${currentStep >= step.id ? 'bg-blue-600 text-white' : 'bg-white border-2 border-gray-300 text-gray-400'}`}
              >
                {step.id}
              </div>
              <div className="text-xs text-center">
                <div className={`font-medium ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-500'}`}>
                  {step.title}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">{renderStep()}</div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={prevStep}
              className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              ← Previous
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!validateStep(currentStep)}
              className={`px-6 py-3 rounded-lg shadow-sm text-sm font-medium text-white ${
                validateStep(currentStep) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Next Step →
            </button>
          ) : (
            <button
              type="submit"
              className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg shadow-sm hover:bg-green-700"
            >
              {isEditing ? 'Update Product' : 'Create Product'}
            </button>
          )}
        </div>
      </div>

      {/* Step Indicator */}
      <div className="text-center text-sm text-gray-500">
        Step {currentStep} of {STEPS.length}
      </div>
    </form>
  );
};

export default ProductForm;
